<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import {useAuthStore} from "~/plugins/store/auth";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import {useNuxtApp} from "#app";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import {useOrderStore} from "~/plugins/store/order";
import type {Order} from "~/types/Order";
import {useRcaStore} from "~/plugins/store/rca";
import type {RcaPolicy} from "~/types/Rca";
import type {HouseInsurancePolicy} from "~/types/House";
import AccountLayout from "~/layouts/AccountLayout.vue";

export default defineComponent({
  components: {AccountLayout, LoadingDialog},
  setup() {
    definePageMeta({
      middleware: [
        'auth',
      ],
    });

    useHead({
      title: 'Xiga | Istoric Comenzi',
    });
    // Utility function to introduce a delay
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({}); // Initialize translations as a reactive reference

    const stateMap = {
      0: translation(translationList.order_placed, 'Plasată'),
      1: translation(translationList.order_initialized, 'Inițializată'),
      2: translation(translationList.order_completed, 'Finalizată'),
      3: translation(translationList.order_abandoned, 'Abandonată'),
      4: translation(translationList.order_processing, 'În procesare'),
      5: translation(translationList.order_failed, 'Eșuată')
    };

    // Funcție care returnează o clasă de culoare în funcție de starea order-ului
    const stateColor = (state) => {
      switch (state) {
        case 0:
          return 'bg-blue lighten-3'; // Placed
        case 1:
          return 'bg-green lighten-3'; // Initialized
        case 2:
          return 'bg-teal lighten-3'; // Completed
        case 3:
          return 'bg-red lighten-3'; // Abandoned
        case 4:
          return 'bg-orange lighten-3'; // Processing
        case 5:
          return 'bg-grey lighten-3'; // Failed
        default:
          return 'bg-grey lighten-1'; // Unknown or default
      }
    };

    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const rcaStore = useRcaStore();

    const orderStore = useOrderStore();
    const ordersData = ref<Order[]>([]);
    const {$toast} = useNuxtApp();
    const showLoadingDialog = ref(true);
    const messageLoadingDialog = ref('Se incarcă...');

    const filters = [
      {id: 'all', text: translation(translationList.all, 'Toate')},
      {id: 'today', text: translation(translationList.today, 'Astăzi')},
      {id: 'last_week', text: translation(translationList.last_week, 'Săptămâna trecută')},
      {id: 'last_month', text: translation(translationList.last_month, 'Luna trecută')},
      {id: 'this_year', text: translation(translationList.this_year, 'Anul acesta')}
    ];
    const selectedFilter = ref(filters[0].id);

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.loading, 'Se incarcă...');

      if (!accessToken) {
        showLoadingDialog.value = false;
        return;
      }
      const tmpOrdersData = await orderStore.fetchOrders(accessToken);
      if (tmpOrdersData) {
        ordersData.value = tmpOrdersData;
      }
      showLoadingDialog.value = false;

    });

    // Funcție care primește un index și returnează valoarea stării
    const orderState = (state) => {
      return stateMap[state] || 'Unknown'; // fallback pentru cazuri neașteptate
    };

    const filteredOrders = computed(() => {
      const today = new Date();

      if (ordersData && ordersData.value === null) {
        return [];
      }
      // Filtrare după criterii
      let orders = ordersData.value.filter((order: Order) => {
        const orderDate = new Date(order.created_at);
        switch (selectedFilter.value) {
          case 'today':
            return orderDate.toDateString() === today.toDateString();
          case 'last_week':
            const lastWeek = new Date(today.setDate(today.getDate() - 7));
            return orderDate >= lastWeek;
          case 'last_month':
            const lastMonth = new Date(today.setMonth(today.getMonth() - 1));
            return orderDate >= lastMonth;
          case 'this_year':
            return orderDate.getFullYear() === today.getFullYear();
          default:
            return true; // All
        }
      });

      // Sortare descrescătoare după `created_at`
      return orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });

    const downloadRcaPolicyPdf = async (rcaPolicy: RcaPolicy) => {
      if (accessToken && rcaPolicy.id) {
        const pdf = await rcaStore.fetchPolicyPdf(rcaPolicy.id, accessToken);
        const blob = new Blob([pdf], {type: 'application/pdf'});
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `polita_rca_${rcaPolicy.series + '_' + rcaPolicy.number}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    };

    const downloadHouseInsurancePolicyPdf = async (houseInsurancePolicy: HouseInsurancePolicy) => {
      $toast.info('Funcționalitatea nu este încă disponibilă.');
      // if (accessToken && houseInsurancePolicy.id) {
      //   const pdf = await rcaStore.fetchPolicyPdf(houseInsurancePolicy.id, accessToken);
      //   const blob = new Blob([pdf], {type: 'application/pdf'});
      //   const url = window.URL.createObjectURL(blob);
      //   const a = document.createElement('a');
      //   a.href = url;
      //   a.download = `polita_house_${houseInsurancePolicy.series + '_' + houseInsurancePolicy.number}.pdf`;
      //   a.click();
      //   window.URL.revokeObjectURL(url);
      // }
    };

    return {
      user,
      accessToken,
      translationList,
      translation,
      ordersData,
      showLoadingDialog,
      messageLoadingDialog,
      filters,
      selectedFilter,
      filteredOrders,
      orderState,
      stateColor,
      downloadRcaPolicyPdf,
      downloadHouseInsurancePolicyPdf,
    }
  }
});
</script>

<template>
  <account-layout class="orders">
    <v-card rounded="xl" variant="elevated" elevation="15" class="w-100 pa-5" min-height="400">
      <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
        <v-row >
          <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
            <h3 class="font-weight-bold">
              {{ translation(translationList.my_orders, 'Comenzile mele') }}</h3>
            <p class="font-weight-regular">
              {{
                translation(translationList.my_orders_helper, 'Aici poți vedea toate comenzile tale plasate și statusul lor actual.')
              }}
            </p>
          </v-col>
        </v-row>

        <v-row class="mb-5">
          <v-chip
              v-for="filter in filters"
              :key="filter"
              :color="selectedFilter === filter.id ? 'primary' : 'grey lighten-1'"
              @click="selectedFilter = filter.id"
              class="ma-2"
          >
            {{ filter.text }}
          </v-chip>
        </v-row>

        <v-skeleton-loader v-if="showLoadingDialog" type="image" class="skeleton-h-250"></v-skeleton-loader>
        <v-expansion-panels v-else rounded="0" variant="accordion"
                            class="mt-3">
          <v-expansion-panel v-if="filteredOrders.length > 0" eager class="w-100" style="padding: 0 !important;"
                             v-for="(order, index) in filteredOrders"
                             :key="index"
          >
            <v-expansion-panel-title>
              <template v-slot:default="{ expanded }">
                <v-row class="d-flex align-center" no-gutters>
                  <v-col class="d-flex justify-start my-1" cols="12" sm="3" md="3">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.order_number, 'No.') }} #{{ order.id }}</h3>
                  </v-col>
                  <v-col class="my-1" cols="12" sm="4" md="4">
                    <v-fade-transition leave-absolute>
                      {{ order.created_at }}
                    </v-fade-transition>
                  </v-col>
                  <v-col class="my-1" cols="12" sm="3" md="3">
                    <v-fade-transition leave-absolute>
                      {{ order.amount }} RON
                    </v-fade-transition>
                  </v-col>
                  <v-col class="my-1" cols="12" sm="2" md="2">
                    <v-fade-transition leave-absolute>
                      <v-card rounded="pill" class="pa-2 text-center" elevation="0" :class="stateColor(order.state)">
                        {{ orderState(order.state) }}
                      </v-card>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-divider></v-divider>
              <v-card rounded="0" flat>
                <v-card-title class="fs-20 fw-600">
                  {{ translation(translationList.products, 'Produse') }}: {{ order.order_order_items.length }}
                </v-card-title>
                <v-card-text class="fs-16 fw-400" v-for="(item,itemIndex) in order.order_order_items"
                             :key="itemIndex">
                  <v-row class="d-flex ">
                    <v-col cols="12" sm="9" md="9">
                      <div class="fs-16 fw-600">{{ itemIndex + 1 }}. {{ item.name }}</div>
                      <div class="fs-16 fw-400" v-if="item.rca_policy">
                        {{ translation(translationList.insurance_company_name, 'Asigurator') }}:
                        {{ item.rca_policy.rca_insurance_company.name }} <br>
                        {{ translation(translationList.registration_plate, 'Număr de înmatriculare') }}:
                        {{ item.request_body.vehicle.registration_plate }} <br>
                        {{ translation(translationList.valid_from, 'Valabil de la') }}:
                        {{ item.rca_policy.valid_from }} <br>
                        {{ translation(translationList.valid_until, 'Valabil până la') }}:
                        {{ item.rca_policy.valid_until }} <br>
                        {{ translation(translationList.green_card_exclusions, 'Țări excluse Cartea Verde') }}:
                        {{ item.rca_policy.green_card_excluded }} <br>
                        {{ translation(translationList.deducted_price, 'Decontare directă') }}:
                        {{ item.rca_policy.is_deducted ? 'Da' : 'Nu' }} <br>
                        <v-btn
                            v-if="item.rca_policy.series && item.rca_policy.number"
                            min-width="0"
                            class="block-on-mobile no-uppercase"
                            size="x-small"
                            variant="plain"
                            rounded="0"
                            prepend-icon="mdi-download"
                            @click="downloadRcaPolicyPdf(item.rca_policy)"
                        >
                          {{ translation(translationList.download_offer, 'Descarcă polița') }}
                        </v-btn>
                      </div>
                      <div class="fs-16 fw-400" v-if="item.house_insurance_policy">
                        {{ item.request_body.selected_offer.type_name }} <br>
                        {{ translation(translationList.number_and_series, 'Număr si serie') }}:
                        {{ item.house_insurance_policy.series }} {{ item.house_insurance_policy.number }}<br>
                        {{ translation(translationList.valid_from, 'Valabil de la') }}:
                        {{ item.house_insurance_policy.valid_from }} <br>
                        {{ translation(translationList.valid_until, 'Valabil până la') }}:
                        {{ item.house_insurance_policy.valid_until }} <br>
                        <v-btn
                            v-if="item.house_insurance_policy.series && item.house_insurance_policy.number"
                            min-width="0"
                            class="block-on-mobile no-uppercase"
                            size="x-small"
                            variant="plain"
                            rounded="0"
                            prepend-icon="mdi-download"
                            @click="downloadHouseInsurancePolicyPdf(item.house_insurance_policy)"
                        >
                          {{ translation(translationList.download_offer, 'Descarcă polița') }}
                        </v-btn>
                      </div>
                      <div class="fs-16 fw-400" v-if="item.travel_policy">
                        {{ translation(translationList.code, 'Code') }}:
                        {{ item.travel_policy.code }} <br>
                        {{ translation(translationList.code, 'Destinatie') }}:
                        {{ item.request_body.country.name }}, {{ item.request_body.continent.value }} <br>
                        {{ translation(translationList.valid_from, 'Valabil de la') }}:
                        {{ item.request_body.valid_from }} <br>
                        {{ translation(translationList.valid_until, 'Valabil până la') }}:
                        {{ item.request_body.valid_until }} <br>
                        {{ translation(translationList.number_of_days, 'Număr de zile') }}:
                        {{ item.request_body.number_of_days }} <br>
                        {{ translation(translationList.transportation_method, 'Metodă transport') }}:
                        {{ item.request_body.transportation_method.value }} <br>
                        {{ translation(translationList.number_of_persons, 'Număr de persoane') }}:
                        {{ item.request_body.number_of_travel_users }} <br>
                        {{ translation(translationList.travel_users, 'Călători') }}:
                        <v-row v-for="(travelUser, travelUserIndex) in item.request_body.travel_users"
                               :key="travelUserIndex">
                          <v-col cols="12" sm="12" md="12">
                            {{ travelUser.first_name }} {{ travelUser.last_name }} - {{ travelUser.cnp.value }}
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="3" md="3" class="text-center">
                      <div class="fs-16 fw-600">{{ item.amount }} RON</div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <v-divider></v-divider>
              <v-card rounded="0" flat v-if="order.payment_method">
                <v-card-title class="fs-20 fw-600">
                  {{ translation(translationList.payment_method, 'Metodă de plată') }}
                </v-card-title>
                <v-card-text class="fs-16 fw-400">
                  {{ order.payment_method.name }}
                </v-card-text>
              </v-card>
              <v-divider></v-divider>
              <v-card rounded="0" flat v-if="order.billing_address">
                <v-card-title class="fs-20 fw-600">
                  {{ translation(translationList.billing_address, 'Adresă de facturare') }}
                </v-card-title>
                <v-card-text class="fs-16 fw-400">
                  {{ order.billing_address.first_name }}
                  {{ order.billing_address.last_name }} <br>
                  {{ order.billing_address.phone_number }} <br>
                  Str. {{ order.billing_address.street }},
                  Nr. {{ order.billing_address.street_number }} <br>
                  {{ order.billing_address.county_name }},
                  {{ order.billing_address.city_name }},
                  {{ order.billing_address.postal_code }}
                </v-card-text>
              </v-card>
            </v-expansion-panel-text>
          </v-expansion-panel>
          <div v-else>
            <v-alert type="info" rounded="0" color="primary">
              {{
                translation(translationList.order_filter_no_match, 'Nu există comenzi care să corespundă criteriilor selectate.')
              }}
            </v-alert>
          </div>
        </v-expansion-panels>

    </v-card>
  </account-layout>
</template>

<style scoped>
.v-expansion-panel--active {
  border: 1px solid rgba(var(--v-theme-gray-dark)); /* Culoarea dorită */
}

</style>
